import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, of, Subscription } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  Filtri,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { LicenzeService } from 'src/app/services/licenze/licenze.service';
import { MaterialitaService } from 'src/app/services/materialita/materialita.service';
import { SettoriService } from 'src/app/services/settori/settori.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaMaterialitaComponent } from './dialog/dialog-crea-materialita/dialog-crea-materialita.component';

@Component({
  selector: 'app-materialita',
  templateUrl: './materialita.component.html',
  styleUrls: ['./materialita.component.scss'],
  standalone: false,
})
export class MaterialitaComponent {
  @ViewChild('tabella') tabella!: ListaTabellareComponent;
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  private _subAziendaCorrente: Subscription | undefined;

  private azienda: Azienda | undefined = undefined;

  public filtri: Filtri[] = [
    {
      titolo: 'Anno',
      forControlName: 'anno',
      input: 'text',
    },
    {
      forControlName: 'settore',
      input: 'multiple-option',
      titolo: 'Settore',
      fnDatiOption: () =>
        this.settoriService.getSettori(0, 1000).pipe(map((value) => value?.content || [])),
      descValueOption: 'titolo',
      idValueOption: 'id',
      riempi: true,
    },
    {
      forControlName: 'stato',
      input: 'multiple-option',
      titolo: 'Stato',
      fnDatiOption: () =>
        of([
          {
            valore: 'PUBBLICATO',
            desc: 'Completata',
          },
          {
            valore: 'BOZZA',
            desc: 'Non completata',
          },
        ]),
      descValueOption: 'desc',
      idValueOption: 'valore',
    },
  ];

  public colonne: { [key: string]: Colonna } = {
    anno: {
      title: 'Anno',
      value: 'anno',
      sortable: true,
    },
    settore: {
      title: 'Settore',
      sortable: true,
      value: (record: any) => record?.settori && record?.settori![0]?.titolo,
    },
    surveyInviate: {
      title: 'Survey Inviate',
      sortable: true,
      value: (record: any) => record?.surveyInviate?.count + '/' + record?.surveyInviate?.tot,
    },
    surveyCompilate: {
      title: 'Survey Compilate',
      sortable: true,
      value: (record: any) => record?.surveyCompilate?.count + '/' + record?.surveyCompilate?.tot,
    },

    dataUltimaConferma: {
      title: 'Ultima Conferma',
      sortable: true,
      value: (record: any) => (record?.dataUltimaConferma ? record?.dataUltimaConferma : ''),
    },
    dataUltimaModifica: {
      title: 'Ultima Modifica',
      value: (record: any) => record?.dataUltimaModifica ?? '',
    },
    dataCreazione: {
      title: 'Data Creazione',
      value: (record: any) => record?.dataCreazione ?? '',
    },
    stato: this.utilityService.getColonnaStato(),
    modifica: {
      type: 'button',
      buttonIcon: (record: any) => (this.isPermessoSoloRead(record) ? 'visibility' : 'edit'),
      title: (record: any) => (this.isPermessoSoloRead(record) ? 'Visualizza' : 'Modifica'),
      buttonId: 'modifica',
      buttonMostraSempre: true,
      sortable: false,
    },
    elimina: {
      ...this.utilityService.getColonnaElimina(),
      nascondiButton: this.nascondiBottoneElimina(),
    },
    elimina_by_developer: {
      type: 'button',
      buttonIcon: 'delete',
      title: 'Elimina by Developer',
      buttonId: 'elimina_by_developer',
      buttonMostraSempre: false,
      sortable: false,
      nascondiButton: (record: any) => !this.isDeveloper || record.stato === 'BOZZA',
    },
  };

  get isDeveloper() {
    return !!this.utenteService.infoUtente?.isDeveloper;
  }
  constructor(
    private readonly settoriService: SettoriService,
    public dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly materialitaService: MaterialitaService,
    private readonly aziendaService: AziendaService,
    public utenteService: UtenteService,
    private readonly licenzeService: LicenzeService,
  ) {}

  nascondiBottoneElimina() {
    if (this.utenteService.infoUtente?.isDeveloper) {
      return false;
    }
    if (this.utenteService.isPermessoAttivo('DELETE_MATERIALITA')) {
      return false;
    }
    return true; //nascondere
  }

  isPermessoSoloRead(record: any) {
    const isEdit = this.utenteService.isPermessoAttivo('EDIT_MATERIALITA');
    const isCreate = this.utenteService.isPermessoAttivo('CREATE_MATERIALITA');
    const statoArchiviato = record.stato === 'ARCHIVIATO';
    return (!isEdit && !isCreate) || statoArchiviato;
  }

  ngAfterViewInit(): void {
    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      let cambiato = false;

      if (this.azienda && azienda && this.azienda.id !== azienda.id) {
        cambiato = true;
      }

      this.azienda = azienda;

      if (cambiato) {
        this.tabella?.caricaDati();
      }
    });
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  creaNuovo(record?: any) {
    if (!record) {
      this.spinnerOver.show();
      this.licenzeService.getUserHasLicense('Materialita').subscribe({
        next: (esito) => {
          this.spinnerOver.hide();
          this.apriDialogCreaMaterialita(record);
        },
        error: (err) => {
          this.spinnerOver.hide();
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: err?.error?.message,
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
    } else {
      this.apriDialogCreaMaterialita(record);
    }
  }

  private apriDialogCreaMaterialita(record: any) {
    const dialogCreaAmbito = this.dialog.open(DialogCreaMaterialitaComponent, {
      data: {
        materialita: record,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '80%',
      height: '99%',
      autoFocus: false,
    });

    dialogCreaAmbito.afterClosed().subscribe((result) => {
      this.tabella.caricaDati();
    });
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    return this.materialitaService.getMaterialita(page, pageSize, ricerca, filters, sortBy);
  };

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'modifica':
        this.creaNuovo(event.data);
        break;
      case 'elimina':
      case 'archivia':
      case 'disarchivia':
        this._elimina(event.data, false);
        break;
      case 'elimina_by_developer':
        this._elimina(event.data, true);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  /**
   * Gestisce l'eliminazione di un modulo tramite il service `utilityService`.
   * Mostra lo spinner durante l'operazione e aggiorna la tabella al termine.
   *
   * @async
   * @param {any} modulo - L'oggetto modulo da eliminare. Deve contenere almeno l'ID e lo stato.
   *
   */
  async _elimina(modulo: any, deleteByDeveloper: boolean) {
    this.spinnerOver.show();

    const success = await this.utilityService.eliminaModulo(
      modulo,
      this.materialitaService,
      deleteByDeveloper,
    );

    this.spinnerOver.hide();

    if (success) {
      this.tabella.caricaDati();
    } else {
      console.log('Operazione annullata o non riuscita');
    }
  }
}
